import classNames from 'classnames'
import * as React from 'react'

import { TooltipInput } from '../../../../components/tooltip-input'
import { getNumberValue } from '../../utils'
import { usePortfolioBulkEditRowContext } from '../PortfolioBulkEditRowContext'
import { EditColumnProps } from './types'

const MAX_PRICE = 999999999

export function CostPerShareEditColumn<
  StateType extends {
    price?: number
  },
  ErrorsType extends {
    price?: boolean
  },
>(props: EditColumnProps<StateType, ErrorsType>) {
  const { isActive, setIsInteracting } = usePortfolioBulkEditRowContext()
  const [rawValue, setRawValue] = React.useState(props.entry.price ?? '')
  const handleBlur = React.useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      setIsInteracting(false)
      const price = ev.currentTarget.valueAsNumber

      if (price > 0 && props.errors?.price) props.setErrors((errors) => ({ ...errors, price: false }))
      if (price > MAX_PRICE) return
      props.setState((entry) => ({ ...entry, price }))
    },
    [props, setIsInteracting]
  )

  const value = getNumberValue(props.entry.price)
  const isNaNValue = isNaN(value)

  let tooltipContent = undefined
  if (props.errors?.price && isActive) {
    if (isNaNValue) tooltipContent = 'Missing price'
    if (value <= 0 || typeof rawValue === 'string') tooltipContent = 'Invalid number, please enter a positive number'
  }

  const showError = props.errors?.price && (isNaN(Number(rawValue)) || Number(rawValue) <= 0 || rawValue === '')

  React.useEffect(() => {
    const priceValue = getNumberValue(props.entry.price)
    if (!isNaN(priceValue)) setRawValue(priceValue)
  }, [props.entry.price])

  return (
    <TooltipInput
      isMobileStyleEnabled={false}
      name="cost-per-share"
      label={props.label}
      isHorizontalLabel={props.isHorizontalLabel}
      labelClassName={props.labelClassName}
      type="number"
      form={props.form}
      error={showError}
      showNumberStepper={false}
      min={0}
      max={MAX_PRICE}
      maxLength={9}
      inputClass={classNames(props.inputClassName, { 'text-right': !props.inputClassName })}
      value={rawValue}
      onChange={(ev) => {
        if (props.updateOnChange !== false) {
          const numberValue = ev.currentTarget.valueAsNumber
          props.setState((entry) => ({ ...entry, price: numberValue }))
        }
        setRawValue(ev.currentTarget.value)
      }}
      onBlur={handleBlur}
      size={props.size}
      tooltipColor="red"
      tooltipSettings={{ unstable_offset: [10, 10] }}
      tooltipContent={showError ? tooltipContent : undefined}
      className={classNames(props.className, { 'is-error': props.errors?.price })}
      isInteractive={isActive}
      onFocus={() => setIsInteracting(true)}
    />
  )
}
